import React, { useState } from 'react';
interface PaymentFormProps {
  initialValues: {
    cardNumber: string;
    nameOnCard: string;
    expiryDate: string;
    cvv: string;
  };
  onSubmit: (values: PaymentFormProps['initialValues']) => void;
  onBack: () => void;
}
export const PaymentForm: React.FC<PaymentFormProps> = ({
  initialValues,
  onSubmit,
  onBack
}) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    // Format card number with spaces
    if (name === 'cardNumber') {
      const formatted = value.replace(/\s/g, '').replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').trim().slice(0, 19); // 16 digits + 3 spaces
      setFormValues(prev => ({
        ...prev,
        [name]: formatted
      }));
    }
    // Format expiry date as MM/YY
    else if (name === 'expiryDate') {
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      let formatted = cleaned;
      if (cleaned.length > 2) {
        formatted = `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
      }
      setFormValues(prev => ({
        ...prev,
        [name]: formatted
      }));
    }
    // Format CVV (numbers only, max 4 digits)
    else if (name === 'cvv') {
      const formatted = value.replace(/\D/g, '').slice(0, 4);
      setFormValues(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormValues(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Remove spaces for validation
    const cardNumberClean = formValues.cardNumber.replace(/\s/g, '');
    if (!cardNumberClean) {
      newErrors.cardNumber = 'Card number is required';
    } else if (cardNumberClean.length < 15) {
      newErrors.cardNumber = 'Card number is incomplete';
    }
    if (!formValues.nameOnCard.trim()) {
      newErrors.nameOnCard = 'Name on card is required';
    }
    if (!formValues.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    } else if (!/^\d{2}\/\d{2}$/.test(formValues.expiryDate)) {
      newErrors.expiryDate = 'Expiry date should be in MM/YY format';
    } else {
      const [month, year] = formValues.expiryDate.split('/');
      const currentYear = new Date().getFullYear() % 100; // Get last 2 digits
      const currentMonth = new Date().getMonth() + 1; // 1-12
      if (parseInt(month) < 1 || parseInt(month) > 12) {
        newErrors.expiryDate = 'Invalid month';
      } else if (parseInt(year) < currentYear || parseInt(year) === currentYear && parseInt(month) < currentMonth) {
        newErrors.expiryDate = 'Card has expired';
      }
    }
    if (!formValues.cvv) {
      newErrors.cvv = 'Security code is required';
    } else if (formValues.cvv.length < 3) {
      newErrors.cvv = 'Security code is incomplete';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formValues);
    }
  };
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-160">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-161">Payment Method</h2>
      <form onSubmit={handleSubmit} data-id="element-162">
        <div className="space-y-6" data-id="element-163">
          <div data-id="element-164">
            <label htmlFor="cardNumber" className="block text-sm font-medium text-gray-700" data-id="element-165">
              Card number
            </label>
            <input type="text" id="cardNumber" name="cardNumber" placeholder="1234 5678 9012 3456" value={formValues.cardNumber} onChange={handleChange} className={`mt-1 block w-full border ${errors.cardNumber ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-fuchsia-500 focus:border-fuchsia-500 sm:text-sm`} data-id="element-166" />
            {errors.cardNumber && <p className="mt-1 text-sm text-red-600" data-id="element-167">{errors.cardNumber}</p>}
          </div>
          <div data-id="element-168">
            <label htmlFor="nameOnCard" className="block text-sm font-medium text-gray-700" data-id="element-169">
              Name on card
            </label>
            <input type="text" id="nameOnCard" name="nameOnCard" value={formValues.nameOnCard} onChange={handleChange} className={`mt-1 block w-full border ${errors.nameOnCard ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-fuchsia-500 focus:border-fuchsia-500 sm:text-sm`} data-id="element-170" />
            {errors.nameOnCard && <p className="mt-1 text-sm text-red-600" data-id="element-171">{errors.nameOnCard}</p>}
          </div>
          <div className="grid grid-cols-2 gap-x-4" data-id="element-172">
            <div data-id="element-173">
              <label htmlFor="expiryDate" className="block text-sm font-medium text-gray-700" data-id="element-174">
                Expiry date (MM/YY)
              </label>
              <input type="text" id="expiryDate" name="expiryDate" placeholder="MM/YY" value={formValues.expiryDate} onChange={handleChange} className={`mt-1 block w-full border ${errors.expiryDate ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-fuchsia-500 focus:border-fuchsia-500 sm:text-sm`} data-id="element-175" />
              {errors.expiryDate && <p className="mt-1 text-sm text-red-600" data-id="element-176">{errors.expiryDate}</p>}
            </div>
            <div data-id="element-177">
              <label htmlFor="cvv" className="block text-sm font-medium text-gray-700" data-id="element-178">
                Security code (CVV)
              </label>
              <input type="text" id="cvv" name="cvv" placeholder="123" value={formValues.cvv} onChange={handleChange} className={`mt-1 block w-full border ${errors.cvv ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-fuchsia-500 focus:border-fuchsia-500 sm:text-sm`} data-id="element-179" />
              {errors.cvv && <p className="mt-1 text-sm text-red-600" data-id="element-180">{errors.cvv}</p>}
            </div>
          </div>
          <div className="flex items-center" data-id="element-181">
            <input id="saveCard" name="saveCard" type="checkbox" className="h-4 w-4 text-fuchsia-600 focus:ring-fuchsia-500 border-gray-300 rounded" data-id="element-182" />
            <label htmlFor="saveCard" className="ml-2 block text-sm text-gray-900" data-id="element-183">
              Save this card for future purchases
            </label>
          </div>
        </div>
        <div className="mt-8 flex justify-between space-x-4" data-id="element-184">
          <button type="button" className="py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fuchsia-500" onClick={onBack} data-id="element-185">
            Back to Shipping
          </button>
          <button type="submit" className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-fuchsia-600 hover:bg-fuchsia-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-fuchsia-500" data-id="element-186">
            Continue to Review
          </button>
        </div>
      </form>
    </div>;
};